import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";

// Servicio para manejar las operaciones relacionadas con los usuarios

// Agregar un nuevo usuario a la base de datos
export const addUser = async (user) => {
	try {
		// Verificar si el nombre de usuario o correo ya existe
		const existingUser = await db.users
			.where("username")
			.equals(user.username)
			.or("email")
			.equals(user.email)
			.first();

		if (existingUser) {
			throw new Error("El nombre de usuario o correo ya está en uso");
		}

		// Agregar el usuario a la base de datos
		const id = await db.users.add({
			firstName: user.firstName,
			lastName: user.lastName,
			username: user.username,
			email: user.email,
			password: user.password, // En una aplicación real, la contraseña debería ser hasheada
			createdAt: new Date(),
			pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
			lastModified: new Date(),
			No: user.No || null, // Guardar el No de Business Central si existe
			deleted: false,
			Admin: user.Admin || false, // Nuevo campo Admin con valor por defecto false
		});

		return { id, ...user };
	} catch (error) {
		console.error("Error al agregar usuario:", error);
		throw error;
	}
};

// Obtener todos los usuarios
export const getAllUsers = async () => {
	try {
		// Obtener solo usuarios no marcados como eliminados
		const users = await db.users.filter((user) => !user.deleted).toArray();
		return users;
	} catch (error) {
		console.error("Error al obtener usuarios:", error);
		throw error;
	}
};

// Obtener un usuario por ID local
export const getUserById = async (id) => {
	try {
		const user = await db.users.get(id);
		if (!user || user.deleted) {
			throw new Error("Usuario no encontrado");
		}
		return user;
	} catch (error) {
		console.error(`Error al obtener usuario con ID ${id}:`, error);
		throw error;
	}
};

// Obtener un usuario por No de Business Central
export const getUserByNo = async (no) => {
	try {
		const user = await db.users
			.where("No")
			.equals(no)
			.and((user) => !user.deleted)
			.first();

		if (!user) {
			throw new Error("Usuario no encontrado");
		}
		return user;
	} catch (error) {
		console.error(`Error al obtener usuario con No ${no}:`, error);
		throw error;
	}
};

// Iniciar sesión de usuario
export const loginUser = async (username, password) => {
	try {
		const user = await db.users
			.where("No")
			.equals(username)
			.and((user) => !user.deleted) // Asegurar que no está eliminado
			.first();

		if (!user) {
			throw new Error("Usuario no encontrado");
		}

		if (user.password !== password) {
			// En una aplicación real, se debería comparar hashes
			throw new Error("Contraseña incorrecta");
		}

		return user;
	} catch (error) {
		console.error("Error al iniciar sesión:", error);
		throw error;
	}
};

// Cambiar contraseña de usuario
export const changePassword = async (userId, currentPassword, newPassword) => {
	try {
		const user = await getUserById(userId);

		if (!user) {
			throw new Error("Usuario no encontrado");
		}

		// Verificar que la contraseña actual sea correcta
		if (user.password !== currentPassword) {
			throw new Error("La contraseña actual es incorrecta");
		}

		// Actualizar la contraseña
		await db.users.update(userId, {
			password: newPassword,
			pendingSync: 1, // Usar 1 en lugar de true
			lastModified: new Date(),
		});

		// Marcar para sincronización
		await markForSync("users", userId);

		return { success: true };
	} catch (error) {
		console.error("Error al cambiar contraseña:", error);
		throw error;
	}
};

// Actualizar un usuario existente
export const updateUser = async (id, userData) => {
	try {
		const user = await db.users.get(id);
		if (!user || user.deleted) {
			throw new Error("Usuario no encontrado");
		}

		// Si no se proporciona una contraseña nueva, mantener la actual
		if (!userData.password) {
			userData.password = user.password;
		}

		// Actualizar el usuario
		await db.users.update(id, {
			...userData,
			lastModified: new Date(),
		});

		// Marcar como pendiente de sincronización
		await markForSync("users", id);

		return await getUserById(id);
	} catch (error) {
		console.error(`Error al actualizar usuario con ID ${id}:`, error);
		throw error;
	}
};

// Eliminar un usuario
export const deleteUser = async (id) => {
	try {
		const user = await db.users.get(id);
		if (!user) {
			throw new Error("Usuario no encontrado");
		}

		// Marcar como eliminado y pendiente de sincronización
		await markForDelete("users", id);

		return true;
	} catch (error) {
		console.error(`Error al eliminar usuario con ID ${id}:`, error);
		throw error;
	}
};
